import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { Palette, ArrowLeft, Link as LinkIcon, ExternalLink, Save, CheckCircle2, Calendar as CalIcon, Image as ImageIcon, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import PrintablesTab from "@/pages/pm/tabs/PrintablesTab";

// Minimalist read-only calendar view for a designer (or SM Manager viewing what
// a designer attached). Click a date → side panel to view/attach a design link.
export default function DesignerEventCalendar() {
  const { eventId } = useParams();
  const nav = useNavigate();
  const { user, customRole } = useAuth();
  const [event, setEvent] = useState(null);
  const [posts, setPosts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [monthStart, setMonthStart] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const canEdit = ["designer", "social_media_manager", "social_media_executive", "admin", "regional_pm", "project_manager"].includes(user.role);
  const [section, setSection] = useState("calendar");

  const load = async () => {
    try {
      const [ev, ps] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/social-posts`),
      ]);
      setEvent(ev.data);
      setPosts(ps.data || []);
    } catch { toast.error("Could not load event"); }
  };
  useEffect(() => { load(); }, [eventId]);

  const postsByDate = useMemo(() => {
    const m = {};
    (posts || []).forEach((p) => {
      const d = (p.scheduled_date || p.post_date || p.date || "").slice(0, 10);
      if (!d) return;
      (m[d] = m[d] || []).push(p);
    });
    return m;
  }, [posts]);

  // Same active window as SM dashboard: created_at → end_date + 5 days.
  const windowRange = useMemo(() => {
    if (!event) return null;
    const startIso = (event.created_at || event.start_date || "").slice(0, 10);
    const endBase = event.end_date || event.start_date;
    if (!startIso || !endBase) return null;
    const endDt = new Date(endBase);
    endDt.setDate(endDt.getDate() + 5);
    // Use LOCAL date components — toISOString() shifts to UTC which puts
    // IST users a day behind (00:00 IST = 18:30 UTC previous day).
    const ey = endDt.getFullYear();
    const em = String(endDt.getMonth() + 1).padStart(2, "0");
    const ed = String(endDt.getDate()).padStart(2, "0");
    return { start: startIso, end: `${ey}-${em}-${ed}` };
  }, [event]);

  if (!event) return <Layout navItems={getNav(user.role, customRole)}><div className="p-12 text-slate-400">Loading…</div></Layout>;

  // Calendar grid — always show 6 weeks
  const first = monthStart;
  const startDow = first.getDay();
  const cells = [];
  const day0 = new Date(first); day0.setDate(1 - startDow);
  for (let i = 0; i < 42; i++) {
    const d = new Date(day0); d.setDate(day0.getDate() + i);
    // Use LOCAL date components — toISOString() would shift to UTC and
    // cause IST users to see a 1-day lag (clicking 17th registers as 16th).
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    const iso = `${y}-${mo}-${da}`;
    cells.push({ date: d, iso, inMonth: d.getMonth() === first.getMonth() });
  }

  const prevMonth = () => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1));
  const nextMonth = () => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1));

  const selectedPosts = selectedDate ? (postsByDate[selectedDate] || []) : [];

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="designer-event-calendar">
        <button onClick={() => nav(-1)} className="text-sm text-slate-500 hover:text-fuchsia-600 flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="dashboard-card p-6 mb-6 flex items-center gap-4">
          {event.logo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${event.logo_url}`} className="w-16 h-16 rounded-2xl object-cover" alt="" /> :
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center text-white font-display font-black text-2xl"><Palette className="w-8 h-8" /></div>}
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-black text-2xl truncate">{event.name}</h1>
            <div className="text-sm text-slate-500">{event.school_name} · {event.start_date} → {event.end_date}</div>
          </div>
        </div>

        <div className="flex gap-2 mb-5">
          {[
            { key: "calendar", label: "Social Media Calendar", Icon: CalIcon },
            { key: "printables", label: "Designs", Icon: Printer },
          ].map((s) => (
            <button key={s.key} onClick={() => setSection(s.key)} data-testid={`designer-section-${s.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${section === s.key ? "bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow" : "bg-white border border-slate-200 text-slate-600"}`}>
              <s.Icon className="w-4 h-4" /> {s.label}
            </button>
          ))}
        </div>

        {section === "printables" ? <PrintablesTab event={event} /> : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 dashboard-card p-5">
            <div className="flex items-center justify-between mb-4">
              <button onClick={prevMonth} className="btn-ghost text-xs px-2">‹</button>
              <div className="font-display font-black text-lg">{monthStart.toLocaleString("default", { month: "long", year: "numeric" })}</div>
              <button onClick={nextMonth} className="btn-ghost text-xs px-2">›</button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400 uppercase mb-2">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((c) => {
                const dayPosts = postsByDate[c.iso] || [];
                const isSelected = selectedDate === c.iso;
                const inWindow = !windowRange || (c.iso >= windowRange.start && c.iso <= windowRange.end);
                return (
                  <button
                    key={c.iso}
                    onClick={() => inWindow && setSelectedDate(c.iso)}
                    disabled={!inWindow}
                    title={!inWindow ? "Outside event window (creation → 5 days after end)" : undefined}
                    className={`aspect-square p-1 rounded-lg text-xs transition-all border ${
                      !inWindow ? "border-transparent bg-slate-50/50 opacity-30 cursor-not-allowed" :
                      isSelected ? "border-fuchsia-500 bg-fuchsia-50" :
                      c.inMonth ? "border-slate-100 hover:border-fuchsia-200" : "border-transparent opacity-40"
                    }`}
                    data-testid={`design-day-${c.iso}`}
                  >
                    <div className="text-right font-bold text-slate-700">{c.date.getDate()}</div>
                    {dayPosts.length > 0 && (
                      <div className="mt-0.5 flex flex-wrap gap-0.5 justify-center">
                        {dayPosts.slice(0, 3).map((p) => (
                          <span key={p.post_id} className={`w-1.5 h-1.5 rounded-full ${p.design_link ? "bg-fuchsia-500" : "bg-orange-400"}`}></span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex items-center gap-4 text-[10px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400"></span> Post scheduled</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-fuchsia-500"></span> Design attached</span>
            </div>
          </div>

          <div className="dashboard-card p-5">
            <h4 className="font-bold flex items-center gap-2"><CalIcon className="w-4 h-4 text-fuchsia-600" /> {selectedDate || "Pick a date"}</h4>
            {!selectedDate ? (
              <p className="text-sm text-slate-400 mt-4">Click any date on the calendar to see scheduled posts and attach a design link.</p>
            ) : selectedPosts.length === 0 ? (
              <p className="text-sm text-slate-400 mt-4">No posts scheduled on this date.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {selectedPosts.map((p) => (
                  <PostCard key={p.post_id} post={p} canEdit={canEdit} onSaved={load} />
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </Layout>
  );
}

function PostCard({ post, canEdit, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ design_link: post.design_link || "", design_thumbnail: post.design_thumbnail || "", design_note: post.design_note || "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/sm-posts/${post.post_id}/design-link`, form);
      toast.success("Design link saved");
      setEdit(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-3 rounded-xl border border-slate-100" data-testid={`design-post-${post.post_id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-slate-800 truncate">{post.title || post.caption?.slice(0, 60) || "Untitled post"}</div>
          <div className="text-[10px] text-slate-400">{post.platform || "—"} · {post.status || "scheduled"}</div>
        </div>
        {post.posted && <CheckCircle2 className="w-4 h-4 text-emerald-500" title="Posted" />}
      </div>

      {post.caption && <div className="text-xs text-slate-600 mt-2 line-clamp-3">{post.caption}</div>}

      {!edit && post.design_link && (
        <div className="mt-3 flex items-center gap-2">
          {post.design_thumbnail ? (
            <a href={post.design_link} target="_blank" rel="noreferrer" className="block relative">
              <img src={post.design_thumbnail} alt="Design" className="w-full max-h-40 object-cover rounded-lg border border-slate-100" />
            </a>
          ) : (
            <a href={post.design_link} target="_blank" rel="noreferrer" className="text-xs text-fuchsia-700 underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Open design</a>
          )}
        </div>
      )}
      {post.design_link && (
        <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500">
          <a href={post.design_link} target="_blank" rel="noreferrer" className="text-fuchsia-700 hover:underline truncate flex items-center gap-1 max-w-[70%]"><LinkIcon className="w-3 h-3" /> {post.design_link}</a>
          {post.design_added_by_name && <span>by {post.design_added_by_name}</span>}
        </div>
      )}

      {edit ? (
        <div className="mt-3 space-y-2">
          <input placeholder="Paste design link (Drive / Canva / OneDrive / etc.)" className="input-pill text-xs" value={form.design_link} onChange={(e) => setForm({ ...form, design_link: e.target.value })} data-testid={`design-link-input-${post.post_id}`} />
          <input placeholder="Thumbnail URL (leave blank to auto-detect)" className="input-pill text-xs" value={form.design_thumbnail} onChange={(e) => setForm({ ...form, design_thumbnail: e.target.value })} />
          <textarea rows={2} placeholder="Notes for the SM team" className="input-pill text-xs" value={form.design_note} onChange={(e) => setForm({ ...form, design_note: e.target.value })} />
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="btn-primary py-1.5 text-xs flex-1 disabled:opacity-50" data-testid={`design-save-${post.post_id}`}><Save className="w-3 h-3" /> {busy ? "Saving…" : "Save"}</button>
            <button onClick={() => setEdit(false)} className="btn-ghost py-1.5 text-xs">Cancel</button>
          </div>
        </div>
      ) : (
        canEdit && (
          <button onClick={() => setEdit(true)} className="mt-3 text-xs text-fuchsia-700 hover:underline inline-flex items-center gap-1" data-testid={`design-attach-${post.post_id}`}>
            <LinkIcon className="w-3 h-3" /> {post.design_link ? "Update design link" : "Attach design link"}
          </button>
        )
      )}
    </div>
  );
}
